import React, { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Share2, UsersRound, X } from 'lucide-react';
import type { AppModule, Group, User } from '../types';
import { userService } from '../services/userService';
import { groupService } from '../services/groupService';
import { moduleService } from '../services/moduleService';

/**
 * This module's own direct link — the exact URL `Portal`'s `openViewer`
 * already builds and pushes into the address bar once it is open (see
 * App.tsx), reconstructed here rather than passed down so this dialog does
 * not need a prop threaded in from three components away for one string.
 * `viewer` is the only param kept: a shared link should be the module and
 * nothing about whatever page it happened to be copied from.
 */
function moduleLink(moduleId: string): string {
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('viewer', moduleId);
  return url.toString();
}

/**
 * Module sharing — by its owner (given `modules:share`) or an admin.
 *
 * Recipients, whether added directly or through a group, get VIEW/USE access
 * only — the receiving user can never edit, rename, or delete a shared
 * module (enforced in modulePermissions.ts). The caller must gate rendering
 * on `canShareModule(currentUser, module, canShareModules)`.
 */
export default function ShareModuleDialog({
  module,
  onClose,
}: {
  module: AppModule;
  onClose: () => void;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [all, shared, allGroups] = await Promise.all([
        userService.listUsers(),
        moduleService.fetchModuleShare(module.id),
        groupService.list(),
      ]);
      // Admins already see every module; sharing targets non-admins.
      setUsers(all.filter((user) => user.role !== 'admin'));
      setSelectedIds(shared.userIds);
      setGroups(allGroups);
      setSelectedGroupIds(shared.groupIds);
    } catch {
      setError('Could not load sharing for this module.');
    } finally {
      setLoading(false);
    }
  }, [module.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(moduleLink(module.id));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy the link — copy it from the box instead.');
    }
  };

  const toggle = (userId: string) =>
    setSelectedIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );

  const toggleGroup = (groupId: string) =>
    setSelectedGroupIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await moduleService.setModuleShare(module.id, selectedIds, selectedGroupIds);
      onClose();
    } catch {
      setError('Could not update sharing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="gl-modal-backdrop fixed inset-0 z-[9500] flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Share ${module.title || module.name}`}
        className="gl-modal w-full max-w-md overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#0D9488]">
              <Share2 className="h-3.5 w-3.5" />
              Share module
            </p>
            <h3 className="mt-0.5 truncate text-base font-semibold text-[#0F2942]">
              {module.title || module.name}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="mb-4">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Direct link
            </span>
            <div className="flex items-center gap-1.5">
              <input
                readOnly
                value={moduleLink(module.id)}
                onFocus={(event) => event.currentTarget.select()}
                aria-label="Direct link to this module"
                className="min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600 outline-none focus:border-teal-400"
              />
              <button
                type="button"
                onClick={() => void copyLink()}
                className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-teal-600" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </>
                )}
              </button>
            </div>
            <p className="mt-1 text-[10.5px] leading-relaxed text-slate-400">
              Opens this module directly, skipping the home page. Only works for someone who is
              already signed in and already has access — this does not grant it.
            </p>
          </div>

          <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
            Selected users and groups can{' '}
            <strong className="font-semibold text-slate-700">view and use</strong> this module. They
            cannot edit, rename, or delete it.
          </p>

          {!loading && groups.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <UsersRound className="h-3.5 w-3.5" />
                Share with a group
              </p>
              <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                {groups.map((group) => {
                  const selected = selectedGroupIds.includes(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => toggleGroup(group.id)}
                      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                        selected ? 'bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#0F2942]">
                        {group.name}
                      </span>
                      <span className="flex-shrink-0 text-[10.5px] text-slate-400">
                        {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-slate-400">Loading users…</p>
          ) : users.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-xs text-slate-400">
              No non-admin users exist yet. Create one in Settings → User Management.
            </p>
          ) : (
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {users.map((user) => {
                const selected = selectedIds.includes(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggle(user.id)}
                    className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                      selected ? 'bg-teal-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                        selected ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {selected && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-medium text-[#0F2942]">
                        {user.name}
                      </span>
                      <span className="block truncate text-[11px] text-slate-400">{user.email}</span>
                    </span>
                    {user.status === 'disabled' && (
                      <span className="flex-shrink-0 text-[10px] uppercase text-slate-400">
                        disabled
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-[11px] text-slate-400">
            {selectedIds.length} {selectedIds.length === 1 ? 'user' : 'users'}
            {selectedGroupIds.length > 0
              ? `, ${selectedGroupIds.length} group${selectedGroupIds.length === 1 ? '' : 's'}`
              : ''}{' '}
            selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || loading}
              className="rounded-lg bg-[#0F2942] px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save sharing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
